import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { ArmorClassBox } from '@dndbeyond/character-components/es';
import {
    CharacterPreferences,
    SpeedInfo,
    RuleData,
    rulesEngineSelectors,
    CharacterTheme,
} from '@dndbeyond/character-rules-engine/es';
import { IRollContext } from '@dndbeyond/dice';

import { sidebarActions } from '../../../../Shared/actions';
import { appEnvSelectors, characterRollContextSelectors } from '../../../../Shared/selectors';
import { PaneComponentEnum } from '../../../../Shared/utils';
import InitiativeBox from '../../../components/InitiativeBox';
import ProficiencyBonusBox from '../../../components/ProficiencyBonusBox';
import SpeedBox from '../../../components/SpeedBox';
import { SheetAppState } from '../../../typings';

interface Props extends DispatchProp {
    armorClass: number;
    speeds: SpeedInfo;
    proficiencyBonus: number;
    initiative: number;
    hasInitiativeAdvantage: boolean;
    preferences: CharacterPreferences;
    theme: CharacterTheme;
    ruleData: RuleData;
    isReadonly: boolean;
    diceEnabled: boolean;
    characterRollContext: IRollContext;
}
class CombatTablet extends React.PureComponent<Props> {
    static defaultProps = {
        diceEnabled: false,
    };

    handleProficiencyBonusClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.PROFICIENCY_BONUS));
    };

    handleSpeedsClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.SPEED_MANAGE));
    };

    handleDefensesSummaryClick = (evt: React.MouseEvent): void => {
        const { dispatch } = this.props;
        evt.stopPropagation();
        evt.nativeEvent.stopImmediatePropagation();
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.DEFENSE_MANAGE));
    };

    handleConditionSummaryClick = (evt: React.MouseEvent): void => {
        const { dispatch } = this.props;
        evt.stopPropagation();
        evt.nativeEvent.stopImmediatePropagation();
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.CONDITION_MANAGE));
    };

    handleArmorClassClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.ARMOR_MANAGE));
    };

    handleInitiativeClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.INITIATIVE));
    };

    render() {
        const {
            preferences,
            proficiencyBonus,
            speeds,
            armorClass,
            initiative,
            hasInitiativeAdvantage,
            theme,
            ruleData,
            diceEnabled,
            characterRollContext,
        } = this.props;

        return (
            <div className="ct-combat-tablet">
                <div className="ct-combat-tablet__extras">
                    <div className="ct-combat-tablet__extra ct-combat-tablet__extra--proficiency">
                        <ProficiencyBonusBox
                            theme={theme}
                            proficiencyBonus={proficiencyBonus}
                            onClick={this.handleProficiencyBonusClick}
                        />
                    </div>
                    <div className="ct-combat-tablet__extra ct-combat-tablet__extra--speed">
                        <SpeedBox
                            speeds={speeds}
                            preferences={preferences}
                            theme={theme}
                            ruleData={ruleData}
                            onClick={this.handleSpeedsClick}
                        />
                    </div>
                    <div className="ct-combat-tablet__extra ct-combat-tablet__extra--initiative">
                        <div className="ct-combat-tablet__initiative">
                            <div
                                className={`ct-combat-tablet__initiative-label ${
                                    theme.isDarkMode ? 'ct-combat-tablet__initiative-label--dark-mode' : ''
                                }`}
                            >
                                Initiative
                            </div>
                            <InitiativeBox
                                theme={theme}
                                initiative={initiative}
                                hasInitiativeAdvantage={hasInitiativeAdvantage}
                                onClick={this.handleInitiativeClick}
                                diceEnabled={diceEnabled}
                                rollContext={characterRollContext}
                            />
                        </div>
                    </div>
                    <div className="ct-combat-tablet__extra ct-combat-tablet__extra--ac">
                        <ArmorClassBox theme={theme} armorClass={armorClass} onClick={this.handleArmorClassClick} />
                    </div>
                    <div className="ct-combat-tablet__extra ct-combat-tablet__extra--statuses">
                        <div className="ct-combat-tablet__ctas">
                            <div className="ct-combat-tablet__cta">
                                <div
                                    className={`ct-combat-tablet__cta-button ${
                                        theme.isDarkMode ? 'ct-combat-tablet__cta-button--dark-mode' : ''
                                    }`}
                                    onClick={this.handleDefensesSummaryClick}
                                >
                                    Defenses
                                </div>
                            </div>
                            <div className="ct-combat-tablet__cta">
                                <div
                                    className={`ct-combat-tablet__cta-button ${
                                        theme.isDarkMode ? 'ct-combat-tablet__cta-button--dark-mode' : ''
                                    }`}
                                    onClick={this.handleConditionSummaryClick}
                                >
                                    Conditions
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: SheetAppState) {
    return {
        initiative: rulesEngineSelectors.getProcessedInitiative(state),
        hasInitiativeAdvantage: rulesEngineSelectors.getHasInitiativeAdvantage(state),
        armorClass: rulesEngineSelectors.getAcTotal(state),
        proficiencyBonus: rulesEngineSelectors.getProficiencyBonus(state),
        speeds: rulesEngineSelectors.getCurrentCarriedWeightSpeed(state),
        preferences: rulesEngineSelectors.getCharacterPreferences(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        diceEnabled: appEnvSelectors.getDiceEnabled(state),
        characterRollContext: characterRollContextSelectors.getCharacterRollContext(state),
    };
}

export default connect(mapStateToProps)(CombatTablet);
